import { buildFeatureViewRoutes, createFeatureViewRouter } from "@price-rescue/ui-components";
import type { DefineComponent } from "vue";

import { adminViewRoutes } from "../views/admin-view-registry.js";
import { decideAdminRouteAccess } from "../route-protection.js";
import { useAdminSessionStore } from "../features/authentication/store.js";
import { adminPinia } from "../stores/pinia.js";

const viewComponents = import.meta.glob<DefineComponent>("../views/**/*.vue");

export const adminVueRoutes = buildFeatureViewRoutes({
  defaultPath: "/installation-dashboard",
  missingViewLabel: "admin",
  viewComponents: withSafeAdminRouteFallbacks(viewComponents),
  viewRoutes: adminViewRoutes,
  toMeta: (route) => ({
    title: route.title,
    navId: route.navId,
  }),
});

export const router = createFeatureViewRouter(adminVueRoutes);

router.beforeEach(async (to) => {
  const session = useAdminSessionStore(adminPinia);
  const routeName = String(to.name ?? to.path);
  writeAdminRouterDebugLog("guard:start", {
    fullPath: to.fullPath,
    routeName,
    sessionStatus: session.status,
  });

  if (isLoginRoute(routeName)) {
    await session.bootstrap({ force: true });
    writeAdminRouterDebugLog("guard:login", {
      nextStatus: session.status,
      returnPath: to.query.returnPath,
    });
    return session.status === "authenticated" ? toRouterReturnPath(to.query.returnPath) : true;
  }

  if (isAuthRoute(routeName)) {
    writeAdminRouterDebugLog("guard:auth-route", { routeName });
    return true;
  }

  await session.bootstrap({ force: session.status === "unauthenticated" });
  writeAdminRouterDebugLog("guard:protected", { nextStatus: session.status });

  if (session.status === "expired") return { name: "session-expired" };
  if (session.status === "revoked") return { name: "role-revoked" };
  if (session.status !== "authenticated") {
    return { path: "/login", query: { returnPath: to.fullPath } };
  }

  const decision = decideAdminRouteAccess(to.path, session.sessionView);
  writeAdminRouterDebugLog("guard:access", { allowed: decision.allowed });
  return decision.allowed ? true : { name: "read-only-admin" };
});

function isAuthRoute(nameOrPath: string): boolean {
  return ["login", "login-denied", "session-expired", "role-revoked", "read-only-admin"].some(
    (route) => nameOrPath.includes(route),
  );
}

function isLoginRoute(nameOrPath: string): boolean {
  return nameOrPath.includes("login") && !nameOrPath.includes("login-denied");
}

function toRouterReturnPath(returnPath: unknown): string {
  if (
    typeof returnPath === "string" &&
    returnPath.startsWith("/") &&
    !returnPath.startsWith("//")
  ) {
    return returnPath.startsWith("/#/") ? returnPath.slice(2) : returnPath;
  }

  return "/installation-dashboard";
}

function writeAdminRouterDebugLog(event: string, details: Record<string, unknown>): void {
  if (import.meta.env.DEV || import.meta.env.VITE_APP_ENV === "dev") {
    void event;
    void details;
  }
}

function withSafeAdminRouteFallbacks(
  components: Record<string, () => Promise<DefineComponent>>,
): Record<string, () => Promise<DefineComponent>> {
  return {
    ...components,
    ...Object.fromEntries(
      adminViewRoutes.map((route) => [
        `../views/__admin-route-fallbacks/${route.id}.vue`,
        loadGenericAdminRouteScreen,
      ]),
    ),
  };
}

async function loadGenericAdminRouteScreen(): Promise<DefineComponent> {
  const module = await import("../views/_shared/AdminRouteScreen.vue");
  return module.default as unknown as DefineComponent;
}
